import React, { useState } from 'react';
import { useSms } from '../hooks/useSms';
import { SmsService } from '../services/smsService';

const SmsTestPage = () => {
  const { sendSms, sendWelcomeSms, loading, result, error } = useSms();
  const [formData, setFormData] = useState({
    phone: '',
    message: 'Hello from Ravana Migration! This is a test SMS.',
    testType: 'single'
  });
  const [validationErrors, setValidationErrors] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.message.trim()) {
      errors.push('Message is required');
    }

    if (!formData.phone.trim()) {
      errors.push('Phone number is required');
    } else if (!SmsService.isValidSriLankanPhone(formData.phone)) {
      errors.push('Please enter a valid Sri Lankan mobile number (07XXXXXXXX)');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSendSms = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const phones = formData.phone.split(',').map(p => p.trim()).filter(p => p);
      await sendSms(formData.message, phones);
    } catch (err) {
      console.error('Failed to send SMS:', err);
    }
  };

  const handleSendWelcomeSms = async () => {
    if (!formData.phone.trim()) {
      setValidationErrors(['Phone number is required for welcome SMS']);
      return;
    }

    if (!SmsService.isValidSriLankanPhone(formData.phone)) {
      setValidationErrors(['Please enter a valid Sri Lankan mobile number (07XXXXXXXX)']);
      return;
    }

    // Create a mock submission object
    const mockSubmission = {
      first_name: 'Test',
      last_name: 'User',
      phone: formData.phone,
      country_selection: { selectedCountry: 'Germany' },
      selected_pathway: 'Job Seeker Visa'
    };

    try {
      await sendWelcomeSms(mockSubmission);
    } catch (err) {
      console.error('Failed to send welcome SMS:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">SMS Test Page</h1>
            <p className="text-gray-600">Test the SMS functionality with Dialog API</p>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              <h3 className="font-semibold mb-2">Please fix the following errors:</h3>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="text-sm">{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Success Result */}
          {result && result.success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="font-semibold">SMS Sent Successfully!</h3>
                  <p className="text-sm mt-1">
                    Total: {result.data.total} | Success: {result.data.success} | Failed: {result.data.failed}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Result */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="font-semibold">Failed to Send SMS</h3>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSendSms} className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number(s)
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="e.g., 0771234567 or 0771234567,0777654321"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter one or more numbers separated by commas. Use format: 07XXXXXXXX or +94XXXXXXXXX
              </p>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length} characters
              </p>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send SMS
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleSendWelcomeSms}
                disabled={loading}
                className="flex-1 bg-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Send Welcome SMS
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Phone Number Format Examples:</h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Single: 0771234567</li>
              <li>• Multiple (comma-separated): 0771234567, 0777654321</li>
              <li>• International: +94771234567</li>
              <li>• 0094 format: 0094771234567</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTestPage;
